import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  MenuItem,
  Radio,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { GET_ACTIVE_POLICY_LIST } from "../../../../apis/settingsApis/policy-apis/policyApis";
import {
  ADD_TRUST_CENTER_CHECK_DOCUMENT,
  GET_TRUST_CENTER_CHECK_DOCUMENTS,
  uploadTrustCenterDocument,
} from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  includesQuery,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  ACCEPT_ATTRIBUTE,
  UPLOAD_HINT,
  validateUpload,
} from "../../data/uploadRules";
import { VISIBILITY, visibilityOptionsFor } from "../../data/resources";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";

const URL_PATTERN = /^https?:\/\/\S+$/;

// Same label rule as the Policies & Documents page.
export const regoditCategoryLabel = (category) =>
  category === "isms document"
    ? "ISMS Document"
    : (category || "").charAt(0).toUpperCase() + (category || "").slice(1);

const SOURCES = {
  knowledge_base: {
    title: "From Regodit",
    subtitle: "Approved policies, and evidence uploaded to your checks.",
  },
  document: {
    title: "Upload new",
    subtitle: "A file that is not in Regodit, like a SOC 2 report.",
  },
  link: {
    title: "Link",
    subtitle: "A page on another site. Links are always public.",
  },
};

const SourceOption = ({ value, selected, onSelect }) => {
  const theme = useTheme();
  const { title, subtitle } = SOURCES[value];
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-label={title}
      tabIndex={0}
      onClick={() => onSelect(value)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(value)}
      sx={{
        flex: 1,
        p: 1.5,
        cursor: "pointer",
        borderRadius: "12px",
        border: `1px solid ${
          selected
            ? theme.customColors.icons.primary
            : theme.customColors.border.main
        }`,
        backgroundColor: selected
          ? theme.customColors.status.info.bg
          : theme.customColors.background.main,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Radio
          checked={selected}
          size="small"
          tabIndex={-1}
          sx={{ p: 0, mt: 0.25 }}
        />
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const withoutExtension = (fileName) => fileName.replace(/\.[^.]+$/, "");

const PickerGroup = ({ title, children }) => {
  const theme = useTheme();
  return (
    <>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          px: 1.5,
          py: 0.75,
          fontWeight: 600,
          color: theme.customColors.text.secondary,
          backgroundColor: theme.customColors.background.hover,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        {title}
      </Typography>
      {children}
    </>
  );
};

const PickerRow = ({ item, selected, onToggle }) => {
  const theme = useTheme();
  const { alreadyAdded } = item;
  return (
    <Stack
      role="listitem"
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={() => !alreadyAdded && onToggle(item)}
      sx={{
        px: 1.5,
        py: 0.75,
        cursor: alreadyAdded ? "default" : "pointer",
        opacity: alreadyAdded ? 0.6 : 1,
        borderBottom: `1px solid ${theme.customColors.border.main}`,
        "&:last-of-type": { borderBottom: "none" },
        "&:hover": alreadyAdded
          ? {}
          : { backgroundColor: theme.customColors.background.hover },
      }}
    >
      <Checkbox
        size="small"
        checked={alreadyAdded || selected}
        disabled={alreadyAdded}
        inputProps={{ "aria-label": item.name }}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(item)}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
          {item.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: "block", color: theme.customColors.text.secondary }}
          noWrap
        >
          {item.caption}
          {alreadyAdded && " · Already added"}
        </Typography>
      </Box>
    </Stack>
  );
};

// Pick approved documents from Policies & Documents, and evidence uploaded
// to checks (the API leaves out people, cloud and policy content checks).
const RegoditPicker = ({ existingPolicyIds, selected, onToggle }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const { data, loading, error } = useQuery(GET_ACTIVE_POLICY_LIST, {
    fetchPolicy: "cache-and-network",
  });
  const checkDocs = useQuery(GET_TRUST_CENTER_CHECK_DOCUMENTS, {
    fetchPolicy: "cache-and-network",
  });

  const policies = useMemo(
    () =>
      (data?.getAllTypePolicies?.activePolicies || [])
        .map((file) => ({
          key: `policy-${file.policyId}`,
          kind: "policy",
          name: file.policyDisplayName,
          caption: regoditCategoryLabel(file.category),
          alreadyAdded: existingPolicyIds.has(file.policyId),
          file,
        }))
        .filter((item) => includesQuery(search, item.name, item.caption)),
    [data, existingPolicyIds, search]
  );
  const evidence = useMemo(
    () =>
      (checkDocs.data?.getTrustCenterCheckDocuments || [])
        .map((doc) => ({
          key: `check-${doc.fileId}`,
          kind: "check",
          name: doc.fileName,
          caption: doc.checkName,
          alreadyAdded: doc.alreadyAdded,
          doc,
        }))
        .filter((item) => includesQuery(search, item.name, item.caption)),
    [checkDocs.data, search]
  );

  const stillLoading =
    (loading && !data) || (checkDocs.loading && !checkDocs.data);
  if (stillLoading) {
    return (
      <Stack spacing={1}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }
  if (error && checkDocs.error) {
    return (
      <Alert severity="error" sx={{ borderRadius: "12px" }}>
        We could not load your documents. Try again, or upload the file instead.
      </Alert>
    );
  }
  const nothing = policies.length === 0 && evidence.length === 0;

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search your documents"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          color: theme.customColors.text.secondary,
        }}
      >
        Approved policies and documents, and evidence uploaded to your checks.
        Evidence on people, cloud and policy content checks is not listed. When
        a new policy version is approved, your Trust Center shares it
        automatically.
      </Typography>
      {(error || checkDocs.error) && (
        <Alert severity="warning" sx={{ mt: 1, borderRadius: "12px" }}>
          {error
            ? "We could not load your policies. Check evidence is listed below."
            : "We could not load your check evidence. Policies are listed below."}
        </Alert>
      )}
      <Box
        role="list"
        sx={{
          mt: 1,
          maxHeight: 300,
          overflowY: "auto",
          borderRadius: "8px",
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        {nothing && (
          <Typography
            variant="body2"
            sx={{ p: 2, color: theme.customColors.text.secondary }}
          >
            {search
              ? "No documents match your search."
              : "No approved documents or check evidence yet. Approve one in Policies & Documents, or upload a file."}
          </Typography>
        )}
        {policies.length > 0 && (
          <PickerGroup title="Policies & Documents">
            {policies.map((item) => (
              <PickerRow
                key={item.key}
                item={item}
                selected={selected.has(item.key)}
                onToggle={onToggle}
              />
            ))}
          </PickerGroup>
        )}
        {evidence.length > 0 && (
          <PickerGroup title="Check evidence">
            {evidence.map((item) => (
              <PickerRow
                key={item.key}
                item={item}
                selected={selected.has(item.key)}
                onToggle={onToggle}
              />
            ))}
          </PickerGroup>
        )}
      </Box>
    </Box>
  );
};

const EMPTY_FORM = {
  name: "",
  description: "",
  url: "",
  file: null,
  visibility: "requestable",
};

// Add resources to a category: from Regodit, a new upload, or a link.
// `initialSource` picks the tab it opens on; `title` replaces the heading
// (the Compliance tab's "Upload report").
const AddResourceDialog = ({
  open,
  categories,
  initialCategoryId,
  initialSource = "knowledge_base",
  title,
  existingPolicyIds,
  onAdd,
  onClose,
}) => {
  const theme = useTheme();
  const [source, setSource] = useState(initialSource);
  const [categoryId, setCategoryId] = useState("");
  // Set when opened from a category's own "+ Add" button.
  const fixedCategory = initialCategoryId
    ? categories.find((c) => c.id === initialCategoryId)
    : null;
  const [selected, setSelected] = useState(new Map());
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [addCheckDocument] = useMutation(ADD_TRUST_CENTER_CHECK_DOCUMENT);

  useEffect(() => {
    if (open) {
      setUploading(false);
      setSource(initialSource);
      setCategoryId(initialCategoryId || categories[0]?.id || "");
      setSelected(new Map());
      setForm(EMPTY_FORM);
      setErrors({});
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setErrors({});
  };

  const toggle = (item) => {
    setErrors({});
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.key)) next.delete(item.key);
      else next.set(item.key, item);
      return next;
    });
  };

  // The dialog stays open (and cannot be closed) while a file uploads.
  const close = () => !uploading && onClose();

  const handleAdd = async () => {
    const nextErrors = {};
    if (!categoryId) nextErrors.categoryId = "Choose a category.";
    if (source !== "knowledge_base" && !form.name.trim())
      nextErrors.name = "Name is required.";
    if (source === "document") {
      const fileProblem = validateUpload(form.file);
      if (fileProblem) nextErrors.file = fileProblem;
    }
    if (source === "link" && !URL_PATTERN.test(form.url.trim()))
      nextErrors.url = "Enter a link that starts with https://";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const base = { categoryId, description: form.description.trim() };
    if (source === "knowledge_base") {
      const picked = [...selected.values()];
      // Check evidence becomes a Trust Center document first, as an upload
      // does; the resource then points at it.
      setUploading(true);
      const added = [];
      try {
        for (const item of picked.filter((i) => i.kind === "check")) {
          const { data } = await addCheckDocument({
            variables: { fileId: item.doc.fileId },
          });
          added.push({ item, stored: data.addTrustCenterCheckDocument });
        }
      } catch (err) {
        setErrors({
          picker: err.message || "Could not add the check evidence. Try again.",
        });
        setUploading(false);
        return;
      }
      setUploading(false);
      onAdd([
        ...picked
          .filter((item) => item.kind === "policy")
          .map(({ file }) => ({
            ...base,
            description: "",
            type: "knowledge_base",
            name: file.policyDisplayName,
            visibility: form.visibility,
            policyId: file.policyId,
            sourceLabel: regoditCategoryLabel(file.category),
          })),
        ...added.map(({ item, stored }) => ({
          ...base,
          description: "",
          type: "document",
          name: withoutExtension(item.doc.fileName).slice(0, 150),
          visibility: form.visibility,
          documentId: stored.documentId,
          fileName: stored.fileName,
        })),
      ]);
    } else if (source === "document") {
      // Store the file first; the resource then points at it.
      setUploading(true);
      let stored;
      try {
        const accessToken = await getAccessTokenSilently();
        const idToken = (await getIdTokenClaims()).__raw;
        stored = await uploadTrustCenterDocument(form.file, {
          accessToken,
          idToken,
        });
      } catch (err) {
        setErrors({ file: err.message || "Could not upload the file." });
        setUploading(false);
        return;
      }
      setUploading(false);
      onAdd([
        {
          ...base,
          type: "document",
          name: form.name.trim(),
          visibility: form.visibility,
          documentId: stored.documentId,
          fileName: stored.fileName,
        },
      ]);
    } else {
      onAdd([
        {
          ...base,
          type: "link",
          name: form.name.trim(),
          visibility: "public",
          url: form.url.trim(),
        },
      ]);
    }
    onClose();
  };

  const addLabel =
    source === "knowledge_base" && selected.size > 1
      ? `Add ${selected.size} resources`
      : "Add resource";
  const busyLabel = source === "knowledge_base" ? "Adding…" : "Uploading…";
  const buttonLabel = uploading ? busyLabel : addLabel;

  const visibilityField = source !== "link" && (
    <TextField
      select
      label="Visibility"
      value={form.visibility}
      onChange={(e) => set({ visibility: e.target.value })}
      helperText={VISIBILITY[form.visibility].help}
      fullWidth
    >
      {visibilityOptionsFor(source).map((value) => (
        <MenuItem key={value} value={value}>
          {VISIBILITY[value].label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title={
          title ||
          (fixedCategory
            ? `Add resource to ${fixedCategory.name}`
            : "Add resource")
        }
        subtitle="From your Regodit documents, a new upload, or a link."
        onClose={close}
      />
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            role="radiogroup"
            aria-label="Where the resource comes from"
          >
            {Object.keys(SOURCES).map((value) => (
              <SourceOption
                key={value}
                value={value}
                selected={source === value}
                onSelect={(next) => {
                  setSource(next);
                  setErrors({});
                }}
              />
            ))}
          </Stack>

          {/* Opened from a category's "+ Add": the category is already chosen,
              so it is only asked for from the main "Add resource" button. */}
          {!fixedCategory && (
            <TextField
              select
              label="Category"
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setErrors({});
              }}
              error={!!errors.categoryId}
              helperText={errors.categoryId}
              fullWidth
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {source === "knowledge_base" && (
            <>
              <RegoditPicker
                existingPolicyIds={existingPolicyIds}
                selected={selected}
                onToggle={toggle}
              />
              {errors.picker && (
                <Alert severity="error" sx={{ borderRadius: "12px" }}>
                  {errors.picker}
                </Alert>
              )}
              {visibilityField}
            </>
          )}

          {source === "document" && (
            <>
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileOutlinedIcon />}
                  sx={outlinedButtonSx(theme)}
                >
                  {form.file ? "Choose another file" : "Choose file"}
                  <input
                    hidden
                    type="file"
                    accept={ACCEPT_ATTRIBUTE}
                    aria-label="Choose file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      // Let the same file be picked again after an error.
                      e.target.value = "";
                      if (!file) return;
                      const problem = validateUpload(file);
                      if (problem) {
                        setForm((prev) => ({ ...prev, file: null }));
                        setErrors({ file: problem });
                        return;
                      }
                      set({
                        file,
                        // Suggest a name from the file name.
                        name: form.name || file.name.replace(/\.[^.]+$/, ""),
                      });
                    }}
                  />
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    color: errors.file
                      ? theme.customColors.status.error.text
                      : theme.customColors.text.secondary,
                  }}
                >
                  {errors.file ||
                    form.file?.name ||
                    `${UPLOAD_HINT}. Never upload evidence, risk registers, scan results or full pen test reports.`}
                </Typography>
              </Box>
              <TextField
                label="Resource name"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                helperText="One line, shown under the name."
                fullWidth
              />
              {visibilityField}
            </>
          )}

          {source === "link" && (
            <>
              <TextField
                label="Resource name"
                placeholder="Privacy Policy"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
              />
              <TextField
                label="URL"
                placeholder="https://"
                value={form.url}
                onChange={(e) => set({ url: e.target.value })}
                error={!!errors.url}
                helperText={errors.url}
                fullWidth
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                helperText="One line, shown under the name."
                fullWidth
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={close}
          disabled={uploading}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            uploading || (source === "knowledge_base" && selected.size === 0)
          }
          onClick={handleAdd}
          sx={primaryButtonSx(theme)}
        >
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddResourceDialog;
